import { CalendarDate } from 'ui/components/DateInput/CalendarDate';
import { LoginStatusModel, notLoggedInStatus } from './models';

export interface OAuth2TokenResponse {
    access_token: string;
    expires_in: number;
    refresh_token: string;
    scope: string;
    token_type: string;
}

export interface OAuth2ErrorResponse {
    error?: string;
    error_description?: string;
}

export class HttpError extends Error {
    public status: number;

    constructor(message: string, status: number) {
        super(message);
        this.status = status;
    }
}

export const checkResponse = async (response: Response) => {
    if (response.status >= 200 && response.status < 300) {
        return response;
    } else if (response.status === 401) {
        // TODO: use gettext here
        throw new HttpError('Invalid Username or Password', response.status);
    } else {
        throw new HttpError('Error logging in, please try again', response.status);
    }
};

/**
 * Interface to provide consistent access to the localStorage variables
 * where we cache the user's OAuth2 authentication token as well as the
 * user's name and username.
 */
class AuthLocalStorageManager {
    get authToken(): string|null { return window.localStorage.getItem('auth_token'); }
    set authToken(token: string|null) { this.storeValue('auth_token', token); }
    get authRedirectTo(): string|null { return window.localStorage.getItem('auth_redirect_to'); }
    set authRedirectTo(redirectTo: string|null) { this.storeValue('auth_redirect_to', redirectTo); }
    set authRedirectToLocation(location: Location) {
        const {pathname, search, hash} = location;
        this.authRedirectTo = `${pathname}${search}${hash}`;
    }
    get username(): string|null { return window.localStorage.getItem('auth_username'); }
    set username(username: string|null) { this.storeValue('auth_username', username); }
    get name(): string|null { return window.localStorage.getItem('auth_name'); }
    set name(name: string|null) { this.storeValue('auth_name', name); }
    get avatarUrl(): string|null { return window.localStorage.getItem('auth_avatar_url'); }
    set avatarUrl(avatarUrl: string|null) { this.storeValue('auth_avatar_url', avatarUrl); }
    get onboardingStatus(): string|null {return window.localStorage.getItem('onboarding_status'); }
    set onboardingStatus(status: string|null) { this.storeValue('onboarding_status', status); }
    get userBirthdate(): CalendarDate|null {
        const date = window.localStorage.getItem('userBirthdate');
        if (date === null || date.length !== 'YYYY-MM-DD'.length || date.charAt(4) !== '-') {
            return null;
        }
        return CalendarDate.fromIsoString(date);
    }
    set userBirthdate(date: CalendarDate|null) {
        if (date) {
            window.localStorage.setItem('userBirthdate', date.isoString);
        } else {
            window.localStorage.removeItem('userBirthdate');
        }
    }

    private storeValue(key: string, value: string|null) {
        if (value === null) {
            window.localStorage.removeItem(key);
        } else {
            window.localStorage.setItem(key, value);
        }
    }
}
export const authLocalStorageManager = new AuthLocalStorageManager();

export const getInitialLoginState = (): LoginStatusModel => {
    // If the user is *just* in the process of logging in, they'll
    // be redirected from the LMS back to LabXchange and an OAuth
    // token will be in the URL hash. Record that now:
    const hashToken = getLoginTokenFromHash(window.location.hash);
    if (hashToken) {
        authLocalStorageManager.authToken = hashToken;
        // However, we don't yet know their name/username, so
        // we can't yet show the user as logged in. We'll return
        // that they aren't logged in and wait for main.tsx to
        // dispatch checkLoginStatus() which will get their name/username
        // and then mark them as logged in:
        return notLoggedInStatus;
    }
    // If the browser has a cached auth token, then we assume
    // the user is correclty logged in and proceed with setting
    // the state accordingly. However, we will also double-check
    // that the user is actually logged in: main.tsx will dispatch
    // checkLoginStatus() which will asynchronously use an API to
    // verify the user's login status and then update the state.
    const token = authLocalStorageManager.authToken;
    const name = authLocalStorageManager.name;
    const username = authLocalStorageManager.username;
    const avatarUrl = authLocalStorageManager.avatarUrl;
    const onboardingStatus = authLocalStorageManager.onboardingStatus;
    if (token && name && username) {
        return {
            name,
            token,
            username,
            avatarUrl: avatarUrl? avatarUrl: '',
            loginErrorMessage: null,
            onboardingStatus,
            acceptedCookieAgreement: undefined,
        };
    } else {
        // The user is definitely not logged in.
        return notLoggedInStatus;
    }
};

export const getLoginTokenFromHash = (hash: string): string|null => {
    const match = hash.match('[#&]access_token=([^&]*)');
    return match && decodeURIComponent(match[1]);
};

export const getAuthHeaders = () => {
    return {
        authorization: `Bearer ${authLocalStorageManager.authToken}`,
    };
};
